import { useMemo } from "react";
import { Building2, Users, Sparkles, X, MapPin, FileText, Info, ContactRound, Phone, Smartphone, Mail, Briefcase } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtInt } from "@/lib/format";

export default function CondominioPanelDialog({ open, onOpenChange, condominio, clientes = [], prospectos = [] }) {
  const nome = condominio?.condominio || "";

  const clientesDoCondo = useMemo(
    () => clientes.filter((c) => (c.condominio || "") === nome),
    [clientes, nome]
  );
  const prospectosDoCondo = useMemo(
    () => prospectos.filter((p) => (p.condominio || "") === nome),
    [prospectos, nome]
  );

  if (!condominio) return null;

  const isProsp = condominio.infra_status === "prospecto";
  const hasIdentificacao = !!(condominio.cnpj || condominio.endereco || condominio.complemento);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-condominio-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Building2 className="w-5 h-5 text-primary shrink-0" />
            <span className="truncate">{nome}</span>
            <Badge variant="outline" className={isProsp ? "bg-amber-500/15 text-amber-500 border-amber-500/40" : "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"}>
              {isProsp ? "Prospecto" : "Atendido"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Identificação e localização */}
        {hasIdentificacao && (
          <div className="rounded-lg border border-border bg-muted/40 p-3 mt-2 space-y-2" data-testid="condo-identificacao">
            {condominio.cnpj && (
              <div className="flex items-start gap-2 text-sm">
                <FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">CNPJ</div>
                  <div className="font-mono tabular-nums">{condominio.cnpj}</div>
                </div>
              </div>
            )}
            {condominio.endereco && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Endereço</div>
                  <div>{condominio.endereco}</div>
                </div>
              </div>
            )}
            {condominio.complemento && (
              <div className="flex items-start gap-2 text-sm">
                <Info className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Complemento</div>
                  <div>{condominio.complemento}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</div>
            <div className="font-heading font-semibold capitalize">{condominio.tipo}</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Apt/Salas</div>
            <div className="font-heading font-semibold tabular-nums">{fmtInt(condominio.qtd_apartamentos || 0)}</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Consultor</div>
            <div className="font-heading font-semibold truncate">{condominio.consultor || "—"}</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Responsável local</div>
            <div className="font-heading font-semibold truncate">{condominio.responsavel || "—"}</div>
          </div>
        </div>

        <Tabs defaultValue="clientes" className="mt-4">
          <TabsList className="grid grid-cols-3 w-full max-w-xl">
            <TabsTrigger value="clientes" data-testid="tab-panel-clientes">
              <Users className="w-4 h-4 mr-2" /> Clientes
              <Badge variant="outline" className="ml-2 tabular-nums">{clientesDoCondo.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="prospectos" data-testid="tab-panel-prospectos">
              <Sparkles className="w-4 h-4 mr-2" /> Prospectos
              <Badge variant="outline" className="ml-2 tabular-nums">{prospectosDoCondo.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="contatos" data-testid="tab-panel-contatos">
              <ContactRound className="w-4 h-4 mr-2" /> Contatos
              <Badge variant="outline" className="ml-2 tabular-nums">
                {Array.isArray(condominio.contatos) ? condominio.contatos.length : 0}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clientes" className="mt-4">
            {clientesDoCondo.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Nenhum cliente vinculado a este condomínio ainda.
              </div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Cliente</TableHead>
                      <TableHead>Vendedor</TableHead>
                      <TableHead>Plano</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>E-mail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientesDoCondo.map((c) => (
                      <TableRow key={c.id} data-testid={`panel-cliente-${c.id}`}>
                        <TableCell className="font-medium">{c.nome_cliente}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{c.vendedor}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{c.plano_escolhido || "—"}</Badge>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">{c.telefone || "—"}</TableCell>
                        <TableCell className="text-sm">{c.email || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="prospectos" className="mt-4">
            {prospectosDoCondo.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Nenhum prospecto vinculado a este condomínio ainda.
              </div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Nome</TableHead>
                      <TableHead>Vendedor</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Contato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prospectosDoCondo.map((p) => (
                      <TableRow key={p.id} data-testid={`panel-prospecto-${p.id}`}>
                        <TableCell className="font-medium">{p.nome}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.vendedor}</TableCell>
                        <TableCell className="text-sm max-w-[220px] truncate" title={p.motivo}>{p.motivo || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{p.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">{p.contato || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="contatos" className="mt-4">
            {(!Array.isArray(condominio.contatos) || condominio.contatos.length === 0) ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Nenhum contato cadastrado. Edite o condomínio e adicione síndico, zelador ou administradora.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="panel-contatos-grid">
                {condominio.contatos.map((c) => {
                  const digits = (v) => String(v || "").replace(/\D/g, "");
                  const zapDigits = digits(c.celular || c.telefone);
                  return (
                    <div key={c.id} className="rounded-lg border border-border bg-background p-3 space-y-2" data-testid={`panel-contato-${c.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{c.nome || "(sem nome)"}</div>
                          {c.cargo && (
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <Briefcase className="w-3 h-3" /> {c.cargo}
                            </div>
                          )}
                        </div>
                        {zapDigits.length >= 10 && (
                          <a
                            href={`https://wa.me/${zapDigits.startsWith("55") ? zapDigits : "55" + zapDigits}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0"
                          >
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/40 text-[10px] cursor-pointer hover:bg-emerald-500/20">
                              WhatsApp
                            </Badge>
                          </a>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        {c.telefone && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="w-3.5 h-3.5 shrink-0" />
                            <a href={`tel:${digits(c.telefone)}`} className="hover:text-foreground truncate">{c.telefone}</a>
                          </div>
                        )}
                        {c.celular && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Smartphone className="w-3.5 h-3.5 shrink-0" />
                            <a href={`tel:${digits(c.celular)}`} className="hover:text-foreground truncate">{c.celular}</a>
                          </div>
                        )}
                        {c.email && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-3.5 h-3.5 shrink-0" />
                            <a href={`mailto:${c.email}`} className="hover:text-foreground truncate">{c.email}</a>
                          </div>
                        )}
                        {c.observacao && (
                          <div className="text-[11px] text-muted-foreground italic border-l-2 border-border pl-2 mt-1">
                            {c.observacao}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
